import {
  ApartmentSectionOrderId,
  BUILTIN_SECTIONS_WITH_PERSISTED_SERVICE_TYPE,
  BUILTIN_STAY_SECTION_IDS,
  BuiltinStaySectionId,
  CustomSectionServiceType,
  IApartmentSectionsOrder,
  IBuiltinStaySectionConfig,
  IBuiltinStaySectionsConfig,
  ICustomSection,
  IRoom,
  IStaticStaySectionsConfig,
  STATIC_STAY_SECTION_IDS,
} from '@smarttypes/hotel';
import { get } from 'lodash';

const EARLY_LATE_SECTION_IDS: BuiltinStaySectionId[] = ['earlyCheckIn', 'lateCheckOut'];
const STAY_ORDER_EXCLUDED_IDS: ApartmentSectionOrderId[] = ['directions'];

function sanitizeStayOrder(stay: ApartmentSectionOrderId[] = []): ApartmentSectionOrderId[] {
  return stay.filter(id => !STAY_ORDER_EXCLUDED_IDS.includes(id));
}

export function toCustomSectionOrderId(sectionId: string): ApartmentSectionOrderId {
  return `custom:${sectionId}`;
}

export function isEarlyLateBuiltinSection(sectionId: BuiltinStaySectionId): boolean {
  return EARLY_LATE_SECTION_IDS.includes(sectionId);
}

export function getBuiltinSectionServiceType(
  apartment: IRoom,
  sectionId: BuiltinStaySectionId,
): CustomSectionServiceType {
  if (isEarlyLateBuiltinSection(sectionId)) {
    const persistedServiceType = get(
      apartment,
      `config.builtinSections.${sectionId}.serviceType`,
    ) as CustomSectionServiceType | undefined;
    if (persistedServiceType === 'info' || persistedServiceType === 'upsell') {
      return persistedServiceType;
    }

    const adjustmentKey = sectionId === 'earlyCheckIn' ? 'earlyCheckIn' : 'lateCheckOut';
    return get(apartment, `apartmentInfo.stayAdjustments.${adjustmentKey}.enabled`, false) ? 'upsell' : 'info';
  }

  return get(apartment, `config.builtinSections.${sectionId}.serviceType`, 'info') as CustomSectionServiceType;
}

export function buildDefaultSectionsOrder(
  customSections: ICustomSection[] = [],
  apartment?: IRoom,
): IApartmentSectionsOrder {
  const infoCustom = customSections
    .filter(section => (section.serviceType ?? 'info') === 'info' && section.sectionId)
    .map(section => toCustomSectionOrderId(section.sectionId as string));

  const upsellCustom = customSections
    .filter(section => section.serviceType === 'upsell' && section.sectionId)
    .map(section => toCustomSectionOrderId(section.sectionId as string));

  const infoBuiltins = apartment
    ? BUILTIN_STAY_SECTION_IDS.filter(id => getBuiltinSectionServiceType(apartment, id) === 'info')
    : BUILTIN_STAY_SECTION_IDS;

  const upsellBuiltins = apartment
    ? BUILTIN_STAY_SECTION_IDS.filter(id => getBuiltinSectionServiceType(apartment, id) === 'upsell')
    : [];

  return {
    stay: sanitizeStayOrder(['amenities', ...infoBuiltins, 'transfer', ...infoCustom]),
    upsell: [...upsellBuiltins, ...upsellCustom],
  };
}

export function resolveSectionsOrder(
  apartment: IRoom,
  customSections: ICustomSection[] = [],
): IApartmentSectionsOrder {
  const persistedStay = get(apartment, 'config.sectionsOrder.stay') as ApartmentSectionOrderId[] | undefined;
  const persistedUpsell = get(apartment, 'config.sectionsOrder.upsell') as ApartmentSectionOrderId[] | undefined;
  const defaults = buildDefaultSectionsOrder(customSections, apartment);

  if (!persistedStay?.length && !persistedUpsell?.length) {
    return defaults;
  }

  return {
    stay: sanitizeStayOrder(persistedStay?.length ? persistedStay : (defaults.stay ?? [])),
    upsell: persistedUpsell?.length ? persistedUpsell : (defaults.upsell ?? []),
  };
}

function sanitizeDisplayNameFields(
  config: IBuiltinStaySectionConfig,
  section: IBuiltinStaySectionConfig,
): void {
  const displayNames = section.displayNames
    ? Object.fromEntries(
        Object.entries(section.displayNames).filter(([, value]) => value !== undefined && value !== ''),
      )
    : undefined;

  if (displayNames && Object.keys(displayNames).length > 0) {
    config.displayNames = displayNames;
    return;
  }

  if (section.displayName) {
    config.displayName = section.displayName;
  }
}

export function resolveBuiltinSectionDisplayName(
  section: IBuiltinStaySectionConfig | undefined,
  lang: string,
): string | undefined {
  if (!section) {
    return undefined;
  }

  const localizedName = section.displayNames?.[lang];
  if (localizedName) {
    return localizedName;
  }

  return section.displayName;
}

export function mergeBuiltinSectionsForLanguageUpdate(
  existing: IBuiltinStaySectionsConfig | undefined,
  incoming: IBuiltinStaySectionsConfig | undefined,
  lang: string,
): IBuiltinStaySectionsConfig | undefined {
  if (!incoming) {
    return existing;
  }

  const merged: IBuiltinStaySectionsConfig = { ...(existing ?? {}) };

  for (const sectionId of BUILTIN_STAY_SECTION_IDS) {
    const incomingSection = incoming[sectionId];
    if (!incomingSection) {
      continue;
    }

    const existingSection = merged[sectionId] ?? {};
    const { displayName, displayNames: _incomingDisplayNames, ...incomingRest } = incomingSection;
    const displayNames = { ...(existingSection.displayNames ?? {}) };

    if (displayName) {
      displayNames[lang] = displayName;
    }

    const nextSection: IBuiltinStaySectionConfig = {
      ...existingSection,
      ...incomingRest,
    };

    if (Object.keys(displayNames).length > 0) {
      nextSection.displayNames = displayNames;
    }
    delete nextSection.displayName;

    merged[sectionId] = nextSection;
  }

  return Object.keys(merged).length > 0 ? merged : undefined;
}

export function sanitizeBuiltinSectionsConfig(
  builtinSections?: IBuiltinStaySectionsConfig,
): IBuiltinStaySectionsConfig | undefined {
  if (!builtinSections) {
    return undefined;
  }

  const sanitized: IBuiltinStaySectionsConfig = {};

  for (const sectionId of BUILTIN_STAY_SECTION_IDS) {
    const section = builtinSections[sectionId];
    if (!section) {
      continue;
    }

    const config: IBuiltinStaySectionConfig = {};

    if (BUILTIN_SECTIONS_WITH_PERSISTED_SERVICE_TYPE.includes(sectionId)) {
      const serviceType = section.serviceType ?? 'info';
      config.serviceType = serviceType;

      if (serviceType === 'upsell') {
        if (section.cover !== undefined) {
          config.cover = section.cover;
        }
        sanitizeDisplayNameFields(config, section);
      }
    } else if (EARLY_LATE_SECTION_IDS.includes(sectionId)) {
      const serviceType = section.serviceType ?? 'info';
      config.serviceType = serviceType;

      if (serviceType === 'upsell') {
        if (section.cover !== undefined) {
          config.cover = section.cover;
        }
        sanitizeDisplayNameFields(config, section);
      } else {
        sanitizeDisplayNameFields(config, section);
      }
    } else {
      if (section.cover !== undefined) {
        config.cover = section.cover;
      }
      sanitizeDisplayNameFields(config, section);
    }

    if (section.active === false) {
      config.active = false;
    }

    if (Object.keys(config).length) {
      sanitized[sectionId] = config;
    }
  }

  return Object.keys(sanitized).length ? sanitized : undefined;
}

export function sanitizeStaticStaySectionsConfig(
  staticStaySections?: IStaticStaySectionsConfig,
): IStaticStaySectionsConfig | undefined {
  if (!staticStaySections) {
    return undefined;
  }

  const sanitized: IStaticStaySectionsConfig = {};

  for (const sectionId of STATIC_STAY_SECTION_IDS) {
    const section = staticStaySections[sectionId];
    if (section?.active === false) {
      sanitized[sectionId] = { active: false };
    }
  }

  return Object.keys(sanitized).length ? sanitized : undefined;
}

export function resolveBuiltinSectionsForEditor(
  apartment: IRoom,
  lang?: string,
): IBuiltinStaySectionsConfig | undefined {
  const fromConfig = get(apartment, 'config.builtinSections') as IBuiltinStaySectionsConfig | undefined;
  const result: IBuiltinStaySectionsConfig = { ...(fromConfig ?? {}) };

  for (const sectionId of EARLY_LATE_SECTION_IDS) {
    const persisted = fromConfig?.[sectionId];
    result[sectionId] = {
      ...persisted,
      serviceType: getBuiltinSectionServiceType(apartment, sectionId),
    };
  }

  for (const sectionId of BUILTIN_SECTIONS_WITH_PERSISTED_SERVICE_TYPE) {
    if (result[sectionId]) {
      result[sectionId] = {
        ...result[sectionId],
        serviceType: result[sectionId]?.serviceType ?? 'info',
      };
    }
  }

  if (lang) {
    for (const sectionId of BUILTIN_STAY_SECTION_IDS) {
      const section = result[sectionId];
      if (!section) {
        continue;
      }
      const displayName = resolveBuiltinSectionDisplayName(section, lang);
      result[sectionId] = {
        ...section,
        ...(displayName ? { displayName } : {}),
      };
    }
  }

  return Object.keys(result).length ? result : undefined;
}

export function resolveStaticStaySectionsForEditor(apartment: IRoom): IStaticStaySectionsConfig | undefined {
  const fromConfig = get(apartment, 'config.staticStaySections') as IStaticStaySectionsConfig | undefined;
  if (!fromConfig) {
    return undefined;
  }

  const result: IStaticStaySectionsConfig = {};

  for (const sectionId of STATIC_STAY_SECTION_IDS) {
    const section = fromConfig[sectionId];
    if (section?.active === false) {
      result[sectionId] = { active: false };
    }
  }

  return Object.keys(result).length ? result : undefined;
}

export function parseCustomSectionOrderId(orderId: string): string | undefined {
  return orderId.startsWith('custom:') ? orderId.slice('custom:'.length) : undefined;
}
